/**
 * Because the built-in Promise API is dumb, here's an impl of the JQuery Deferred API, wrapped around the built-in Promise.
 * This make it easier to have control over resolving/rejecting custom Promises
 *
 * Keep the Deferred object private, so not anyone can resolve/reject, but pass out the Promise to anyone who wants to subscibe
 *
 * Usage:
 * ```
 * const dfd = new Deferred<string>()
 * //you can also use await on the promise because this is a standard javascript Promise
 * dfd.getPromise().then(result => console.log(`resulg: ${result}`))
 * console.log(dfd.getStatus()) // prints 'Pending'
 * dfd.resolve('Helios') //prints 'result: Helios', but not syncronously (happens on the next event loop)
 * console.log(dfd.getStatus()) // prints 'Resolved'
 * ```
 */

export class Deferred<T> {
  private readonly _promise: Promise<T>
  private _status: 'Pending' | 'Resolved' | 'Rejected' = 'Pending'
  private _resolve?: (t: T | PromiseLike<T>) => void
  private _reject?: (e: unknown) => void

  constructor() {
    this._promise = new Promise<T>((resolve, reject) => {
      this._resolve = resolve
      this._reject = reject
    })
  }

  getPromise(): Promise<T> {
    return this._promise
  }

  /**
   * Provided for your debugging convenience.
   * Caveat: the status will change immedately after resolve/reject,
   *   but the promise handlers will not be called until the next event loop
   *
   * @returns status
   */
  getStatus(): 'Pending' | 'Resolved' | 'Rejected' {
    return this._status
  }

  resolve(t: T): void {
    if (!this._resolve)
      throw new Error(`_resolve is undefined, this will never happen`) //appease the typescript gods
    if (this._status != 'Pending') return //ignore it

    this._status = 'Resolved'
    this._resolve(t)
  }

  reject(error: Error): void {
    if (!this._reject)
      throw new Error(`_reject is undefined, this will never happen`) //appease the typescript gods
    if (this._status != 'Pending') return //ignore it

    this._status = 'Rejected'
    this._reject(error)
  }
}
